import { useEffect, useMemo, useState } from 'react'
import { Terminal } from './Terminal'

type TypedStep = { kind: 'cmd' | 'out'; text: string; speed?: number; pause?: number; cls?: string }

export function TypedTerminal({ steps, scenarios, title = 'instadash@local', onScenarioChange, initialScenario = 0 }: {
  steps?: TypedStep[]
  scenarios?: TypedStep[][]
  title?: string
  onScenarioChange?: (idx: number) => void
  initialScenario?: number
}) {
  const [scenarioIdx, setScenarioIdx] = useState(initialScenario)
  const [step, setStep] = useState(0)
  const [charsShown, setCharsShown] = useState(0)
  const [clearing, setClearing] = useState(false)

  const currentSteps = useMemo(
    () => scenarios ? scenarios[scenarioIdx % scenarios.length] : (steps ?? []),
    [scenarios, scenarioIdx, steps],
  )

  useEffect(() => { onScenarioChange?.(scenarioIdx) }, [scenarioIdx, onScenarioChange])

  useEffect(() => {
    if (clearing || step >= currentSteps.length) return
    const target = currentSteps[step].text.length
    if (charsShown < target) {
      const t = setTimeout(() => setCharsShown(c => c + 1), currentSteps[step].speed || 14)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => { setStep(s => s + 1); setCharsShown(0) }, currentSteps[step].pause || 800)
    return () => clearTimeout(t)
  }, [step, charsShown, currentSteps, clearing])

  useEffect(() => {
    if (clearing || step < currentSteps.length) return
    if (scenarios) {
      const t = setTimeout(() => {
        setClearing(true)
        setTimeout(() => {
          setScenarioIdx(i => (i + 1) % scenarios.length)
          setStep(0)
          setCharsShown(0)
          setClearing(false)
        }, 500)
      }, 1800)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => { setStep(0); setCharsShown(0) }, 2400)
    return () => clearTimeout(t)
  }, [step, currentSteps.length, scenarios, clearing])

  return (
    <Terminal title={title}>
      {clearing
        ? <div><span className="cursor"></span></div>
        : currentSteps.slice(0, step + 1).map((s, i) => {
            const text = i === step ? s.text.slice(0, charsShown) : s.text
            const showCursor = i === step
            return (
              <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {s.kind === 'cmd' && <span className="ps">$ </span>}
                {s.kind === 'out' && <span className="ps">→ </span>}
                <span className={s.cls || ''}>{text}</span>
                {showCursor && <span className="cursor"></span>}
              </div>
            )
          })
      }
    </Terminal>
  )
}
